import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Spinner from '../../../components/ui/Spinner';
import { getErrorMessage } from '../../../lib/apiClient';
import { getSourcePdf, replaceFields, signatureKeys } from '../signaturesApi';
import { boxToPct, clampPct } from '../lib/coords';
import { FIELD_TYPES, fieldType, signerColor } from '../lib/fieldTypes';
import { loadPdf } from '../lib/pdf';
import FieldBox from './FieldBox';
import FieldPalette from './FieldPalette';
import PdfPage from './PdfPage';
import RecipientSelect from './RecipientSelect';


/**
 * Wizard step 2 — the field placement editor: pdf.js-rendered pages with a
 * percentage-positioned overlay of draggable/resizable field boxes, a palette
 * on the left, and a properties panel for the selected field on the right
 * (OtterWaiver's UploadAgreementNew, rebuilt on pointer events + Tailwind).
 */
export default function StepFields({ document: doc, signers, initialFields, onDone }) {
  const isAgreement = doc.type === 'agreement';
  const [pdf, setPdf] = useState(null);
  const [fields, setFields] = useState(() =>
    (initialFields ?? []).map((f) => ({
      ...f,
      posTop: Number(f.posTop),
      posLeft: Number(f.posLeft),
      posWidth: Number(f.posWidth),
      posHeight: Number(f.posHeight),
    })),
  );
  const [selectedId, setSelectedId] = useState(null);
  const [armedKind, setArmedKind] = useState(null);
  const [signerId, setSignerId] = useState(signers[0]?.id ?? null);

  // Render pages at the scroll pane's width so the PDF always fits — no
  // horizontal scrolling regardless of window size.
  const paneRef = useRef(null);
  const [renderWidth, setRenderWidth] = useState(760);
  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      // contentRect already excludes the pane's p-6 padding.
      const width = Math.floor(entry.contentRect.width);
      if (width > 0) setRenderWidth(width);
    });
    observer.observe(pane);
    return () => observer.disconnect();
    // The pane only exists once `pdf` resolves — until then this component
    // renders a spinner and paneRef is null, so re-subscribe on that flip.
  }, [pdf]);

  const pdfBytes = useQuery({
    queryKey: signatureKeys.sourcePdf(doc.id),
    queryFn: () => getSourcePdf(doc.id),
    staleTime: Infinity,
  });

  useEffect(() => {
    let cancelled = false;
    if (pdfBytes.data) {
      loadPdf(pdfBytes.data).then((loaded) => {
        if (!cancelled) setPdf(loaded);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [pdfBytes.data]);

  const save = useMutation({
    mutationFn: () =>
      replaceFields({
        id: doc.id,
        pagesInPdf: pdf?.numPages ?? doc.pagesInPdf ?? 1,
        fields: fields.map((f) => ({
          ...(isAgreement ? { signerId: f.signerId } : {}),
          kind: f.kind,
          label: f.label || undefined,
          showLabel: f.showLabel ?? true,
          required: f.required ?? true,
          pageNumber: f.pageNumber,
          posTop: f.posTop,
          posLeft: f.posLeft,
          posWidth: f.posWidth,
          posHeight: f.posHeight,
          fontSize: f.fontSize ?? 12,
          radioGroup: f.radioGroup || undefined,
        })),
      }),
    onSuccess: () => onDone(),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const selected = fields.find((f) => f.id === selectedId) ?? null;
  const colorOf = (field) => {
    if (!isAgreement) return signerColor(0);
    const index = signers.findIndex((s) => s.id === field.signerId);
    return signerColor(index === -1 ? 0 : signers[index].sequence);
  };

  const placeField = (kind, pageNumber, pageRect, clientX, clientY) => {
    const meta = fieldType(kind);
    if (!meta) return;
    const widthPx = (meta.defaults.posWidth / 100) * pageRect.width;
    const heightPx = (meta.defaults.posHeight / 100) * pageRect.height;
    const pct = boxToPct(
      {
        x: clientX - pageRect.left - widthPx / 2,
        y: clientY - pageRect.top - heightPx / 2,
        width: widthPx,
        height: heightPx,
      },
      pageRect.width,
      pageRect.height,
    );
    const field = {
      id: crypto.randomUUID(),
      kind,
      signerId: isAgreement ? signerId : null,
      label: '',
      // Off by default: most fields read fine as a bare value on the sealed page,
      // and a caption is easy to switch on for the ones that don't.
      showLabel: false,
      required: true,
      pageNumber,
      fontSize: 12,
      radioGroup: kind === 'radio' ? 'choices' : null,
      ...pct,
    };
    setFields((prev) => [...prev, field]);
    setSelectedId(field.id);
    setArmedKind(null);
  };

  const patchField = (id, patch) =>
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const deleteField = (id) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
    setSelectedId((current) => (current === id ? null : current));
  };

  const pageNumbers = useMemo(
    () => (pdf ? Array.from({ length: pdf.numPages }, (_, i) => i + 1) : []),
    [pdf],
  );

  if (pdfBytes.isLoading || !pdf) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    // Fixed to the viewport so the page itself never scrolls — only the PDF
    // pane below does, keeping the palette and properties panel always visible.
    <div className="flex h-[calc(100vh-13rem)] gap-5">
      {/* Palette */}
      <aside className="w-56 shrink-0 space-y-4 overflow-y-auto">
        {isAgreement && (
          <RecipientSelect signers={signers} value={signerId} onChange={setSignerId} />
        )}
        <FieldPalette armedKind={armedKind} onArm={setArmedKind} />
      </aside>

      {/* Pages — a visible scrollbar is the whole scroll affordance here. */}
      <div
        ref={paneRef}
        className={`scrollbar-always min-h-0 min-w-0 flex-1 space-y-6 overflow-y-scroll rounded-xl bg-slate-200/60 p-6 ${
          armedKind ? 'cursor-crosshair' : ''
        }`}
        onClick={() => setSelectedId(null)}
      >
        {pageNumbers.map((pageNumber) => (
          <div
            key={pageNumber}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              const kind = e.dataTransfer.getData('application/x-sign-field');
              if (!kind) return;
              e.preventDefault();
              const rect = e.currentTarget.querySelector('[data-page-number]').getBoundingClientRect();
              placeField(kind, pageNumber, rect, e.clientX, e.clientY);
            }}
            onClick={(e) => {
              if (!armedKind) return;
              e.stopPropagation();
              const rect = e.currentTarget.querySelector('[data-page-number]').getBoundingClientRect();
              placeField(armedKind, pageNumber, rect, e.clientX, e.clientY);
            }}
          >
            <PdfPage pdf={pdf} pageNumber={pageNumber} width={renderWidth}>
              {fields
                .filter((f) => f.pageNumber === pageNumber)
                .map((field) => (
                  <FieldBox
                    key={field.id}
                    field={field}
                    color={colorOf(field)}
                    selected={field.id === selectedId}
                    onSelect={setSelectedId}
                    onChange={patchField}
                    onDelete={deleteField}
                  />
                ))}
            </PdfPage>
            <p className="mt-1 text-center text-xs text-slate-400">
              Page {pageNumber} of {pdf.numPages}
            </p>
          </div>
        ))}
      </div>

      {/* Properties + save */}
      <aside className="w-60 shrink-0 space-y-4 overflow-y-auto">
        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-100 p-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Field settings</h3>
            {selected && (
              <p className="mt-0.5 text-xs text-slate-400">
                {fieldType(selected.kind)?.label ?? selected.kind}
              </p>
            )}
          </div>
          {selected ? (
            <>
              <Input
                label="Label"
                placeholder={fieldType(selected.kind)?.label ?? selected.kind}
                value={selected.label ?? ''}
                maxLength={120}
                onChange={(e) => patchField(selected.id, { label: e.target.value })}
              />
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={selected.showLabel ?? true}
                  onChange={(e) => patchField(selected.id, { showLabel: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                />
                Show label to PDF
              </label>
              <Input
                label="Font size"
                type="number"
                min={6}
                max={72}
                value={selected.fontSize ?? 12}
                onChange={(e) =>
                  patchField(selected.id, { fontSize: clampPct(Number(e.target.value), 6, 72) })
                }
              />
              {selected.kind === 'radio' && (
                <Input
                  label="Option group"
                  hint="Radios sharing a group act as one choice"
                  value={selected.radioGroup ?? ''}
                  maxLength={64}
                  onChange={(e) => patchField(selected.id, { radioGroup: e.target.value })}
                />
              )}
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={selected.required ?? true}
                  onChange={(e) => patchField(selected.id, { required: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                />
                Required
              </label>
              <Button variant="danger" size="sm" onClick={() => deleteField(selected.id)}>
                Remove field
              </Button>
            </>
          ) : (
            <p className="text-xs leading-relaxed text-slate-400">
              Click a field on the document to edit its label, size, and requirement.
              {isAgreement &&
                ' Every recipient needs at least one signature field before you can publish.'}
            </p>
          )}
        </div>

        <Button className="w-full" loading={save.isPending} onClick={() => save.mutate()}>
          Save & continue
        </Button>
        <p className="text-center text-xs text-slate-400">
          {fields.length} field{fields.length === 1 ? '' : 's'} placed
        </p>
      </aside>
    </div>
  );
}
